import { useCallback, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { alertActions } from '../redux/AlertController';

export const useSmartAssistant = () => {
    const dispatch = useDispatch();
    const user = useSelector((state) => state.userController.user);
    const [isLoading, setIsLoading] = useState(false);
    const [insight, setInsight] = useState(null);
    const [history, setHistory] = useState([]);
    const [isHistoryLoading, setIsHistoryLoading] = useState(false);
    const [isChatLoading, setIsChatLoading] = useState(false);

    const generateInsight = useCallback(async ({ startDate, endDate, timeframe, notes }) => {
        if (!user?.token) return null;

        try {
            setIsLoading(true);
            const response = await fetch(
                `${process.env.REACT_APP_API_BASE_URL}/api/admin/insights/generate`,
                {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                        Authorization: `Bearer ${user.token}`,
                    },
                    body: JSON.stringify({ startDate, endDate, timeframe, notes }),
                }
            );

            const json = await response.json();
            if (!response.ok) {
                throw new Error(json.error || 'فشل توليد التحليل');
            }

            setInsight(json);
            setHistory((prev) => [json, ...prev]);
            return json;
        } catch (error) {
            dispatch(alertActions.showAlert({ msg: error.message, type: 'error' }));
            return null;
        } finally {
            setIsLoading(false);
        }
    }, [dispatch, user]);

    const fetchHistory = useCallback(async () => {
        if (!user?.token) return null;

        try {
            setIsHistoryLoading(true);
            const response = await fetch(
                `${process.env.REACT_APP_API_BASE_URL}/api/admin/insights/history`,
                {
                    headers: {
                        Authorization: `Bearer ${user.token}`,
                    },
                }
            );

            const json = await response.json();
            if (!response.ok) {
                throw new Error(json.error || 'فشل تحميل السجل');
            }

            setHistory(json.insights || []);
            return json;
        } catch (error) {
            dispatch(alertActions.showAlert({ msg: error.message, type: 'error' }));
            return null;
        } finally {
            setIsHistoryLoading(false);
        }
    }, [dispatch, user]);

    const fetchInsightById = useCallback(async (id) => {
        if (!user?.token) return null;

        try {
            setIsLoading(true);
            const response = await fetch(
                `${process.env.REACT_APP_API_BASE_URL}/api/admin/insights/${id}`,
                {
                    headers: {
                        Authorization: `Bearer ${user.token}`,
                    },
                }
            );

            const json = await response.json();
            if (!response.ok) {
                throw new Error(json.error || 'فشل تحميل التقرير');
            }

            setInsight(json);
            return json;
        } catch (error) {
            dispatch(alertActions.showAlert({ msg: error.message, type: 'error' }));
            return null;
        } finally {
            setIsLoading(false);
        }
    }, [dispatch, user]);

    const sendChatMessage = useCallback(async (insightId, message) => {
        if (!user?.token || !insightId) return null;

        try {
            setIsChatLoading(true);
            const response = await fetch(
                `${process.env.REACT_APP_API_BASE_URL}/api/admin/insights/${insightId}/chat`,
                {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                        Authorization: `Bearer ${user.token}`,
                    },
                    body: JSON.stringify({ message }),
                }
            );

            const json = await response.json();
            if (!response.ok) {
                throw new Error(json.error || 'فشل إرسال الرسالة');
            }

            setInsight((prev) => (prev && prev._id === insightId ? { ...prev, chatMessages: json.chatMessages } : prev));
            return json;
        } catch (error) {
            dispatch(alertActions.showAlert({ msg: error.message, type: 'error' }));
            return null;
        } finally {
            setIsChatLoading(false);
        }
    }, [dispatch, user]);

    return {
        insight,
        history,
        isLoading,
        isHistoryLoading,
        isChatLoading,
        generateInsight,
        fetchHistory,
        fetchInsightById,
        sendChatMessage,
    };
};
